// Despachos de Turbo: cada turbo que entró y qué pasó con su oferta. Lo que un
// coordinador tiene que ver primero es el que está SIN chofer y cuánto lleva
// esperando; desde acá se vuelve a ofrecer o se asigna a mano.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import { confirmarConTurboApagado } from "../lib/turboApagado";
import { turboWarning } from "../lib/turboWarnings";

// SCHEDULED: programado (Mercado Libre lo prometió para otro día); sale a ofrecerse ese día.
type DispatchStatus = "SCHEDULED" | "OFFERING" | "ASSIGNED" | "ESCALATED" | "CANCELLED";
type Dispatch = {
  route_id: number; delivery_id: number; store_id: number | null; purchase_at: string; deadline_at: string; distance_km: string | number | null;
  status: DispatchStatus; ring: number; warnings: string[]; escalated_at: string | null; created_at: string;
  route_status: string; driver_id: number | null; scheduled_date: string; driver_username: string | null; driver_first_name: string | null; driver_last_name: string | null;
  store_name: string | null; tracking: string | number | null; nombre_destinatario: string | null; direccion: string | null;
  offers_count: number; declined_count: number; waiting_seconds: number | null; seconds_to_deadline: number;
  // El grupo del chofer en el que corre (varios Turbos con un solo retiro por tienda).
  turbo_group_id?: number | null;
  // LOTE: un lote de programados de una tienda; OFFERING mientras se ofrece entero.
  turbo_group_kind?: "AUTO" | "LOTE" | "MANUAL" | null; turbo_group_status?: "OPEN" | "OFFERING" | "SPLIT" | null;
};
type Offer = { id: number; driver_id: number; username: string; first_name: string | null; last_name: string | null; ring: number; source?: "STORE" | "NEAREST" | "MANUAL"; search_ring?: number | null; distance_km: string | number | null; offered_at: string; expires_at: string; responded_at: string | null; response: string | null };
type Driver = { id: number; username: string; first_name: string | null; last_name: string | null; vehiculo?: string | null };

const REFRESCO_MS = 15_000;
const STATUS: Record<DispatchStatus, { label: string; color: string }> = {
  SCHEDULED: { label: "Programado", color: "#39c5cf" },
  OFFERING: { label: "En oferta", color: "var(--warning, #d29922)" },
  ASSIGNED: { label: "Asignado", color: "var(--primary, #4f8cff)" },
  ESCALATED: { label: "Escalado", color: "var(--danger)" },
  CANCELLED: { label: "Cancelado", color: "var(--text-secondary)" },
};
const ROUTE_LABEL: Record<string, string> = { PLANNED: "planificado", ASSIGNED: "asignado", LOADED: "cargado", IN_PROGRESS: "en curso", COMPLETED: "terminado", CANCELLED: "cancelado" };
const RESPONSE_LABEL: Record<string, { label: string; color: string }> = {
  ACCEPTED: { label: "Aceptó", color: "var(--success)" }, DECLINED: { label: "Pasó", color: "var(--danger)" },
  EXPIRED: { label: "No contestó", color: "var(--text-secondary)" }, SUPERSEDED: { label: "Lo tomó otro", color: "var(--text-secondary)" },
};

const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const kpiStyle: React.CSSProperties = { fontSize: "28px", fontWeight: 800, lineHeight: 1.1, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", verticalAlign: "top" };
// El estado lleva el chip, el detalle del recorrido y los avisos: sin un ancho
// mínimo se apila palabra por palabra y estira la fila entera.
const tdEstadoStyle: React.CSSProperties = { ...tdStyle, minWidth: "210px" };
const selectStyle: React.CSSProperties = { width: "100%", minHeight: "48px", padding: "0 14px", borderRadius: "16px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)" };
const panelStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "18px", background: "rgba(255,255,255,0.03)", padding: "14px" };
const linkBtnStyle: React.CSSProperties = { background: "none", border: "none", padding: 0, marginTop: "4px", color: "var(--primary, #4f8cff)", fontSize: "12px", cursor: "pointer", textDecoration: "underline" };
const badge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${color}`, color, whiteSpace: "nowrap" });
const chip = (on: boolean): React.CSSProperties => ({ padding: "6px 12px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, cursor: "pointer", border: `1px solid ${on ? "var(--primary, #4f8cff)" : "var(--border)"}`, color: on ? "var(--primary, #4f8cff)" : "var(--text-secondary)", background: "transparent" });

const fmtTime = (v: string | null | undefined) => (v ? new Date(v).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "—");
const fmtMin = (s: number) => { const m = Math.round(s / 60); return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`; };
const km = (v: string | number | null) => (v == null ? "—" : `${Number(v).toFixed(1)} km`);
const driverName = (d: { username: string; first_name?: string | null; last_name?: string | null } | null | undefined) => (d ? [d.first_name, d.last_name].filter(Boolean).join(" ") || d.username : "—");
const sinChofer = (d: Dispatch) => d.status === "OFFERING" || d.status === "ESCALATED";

function Modal({ title, onClose, children, width = "820px" }: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; width?: string }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", zIndex: 100, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: width }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
            <h3 style={{ margin: 0, fontSize: "18px", color: "var(--text-primary)" }}>{title}</h3>
            <Button variant="secondary" onClick={onClose}>Cerrar</Button>
          </div>
          {children}
        </Card>
      </div>
    </div>
  );
}

// Mismo selector que en Recorridos: buscador + lista, para no elegir a ciegas
// entre ochenta choferes.
function DriverPicker({ drivers, value, onChange, disabled }: { drivers: Driver[]; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const [q, setQ] = useState("");
  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? drivers.filter((d) => [d.first_name, d.last_name, d.username].filter(Boolean).join(" ").toLowerCase().includes(t)) : drivers;
  }, [drivers, q]);
  const etiqueta = (d: Driver) => `${driverName(d)} · ${d.username}${d.vehiculo ? ` · ${d.vehiculo}` : ""}`;
  return (
    <div>
      <input id="turbo-driver-search" style={{ ...selectStyle, minHeight: "36px" }} placeholder="Buscar por nombre o usuario" value={q} onChange={(e) => setQ(e.target.value)} disabled={disabled} />
      <select id="turbo-driver-select" style={{ ...selectStyle, marginTop: "6px" }} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        <option value="">Elegí un chofer</option>
        {visibles.map((d) => <option key={d.id} value={d.id}>{etiqueta(d)}</option>)}
      </select>
    </div>
  );
}

export default function TurboDispatchesPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const req = useCallback(async (path: string, init?: RequestInit) => {
    const res = await authFetch(path, { ...(init ?? {}), token, json: init?.body !== undefined });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.ok === false) throw new Error(json?.error || `Error ${res.status}`);
    return json;
  }, [token]);

  const [items, setItems] = useState<Dispatch[]>([]);
  const [status, setStatus] = useState<DispatchStatus | "">("");
  const [date, setDate] = useState("");
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actualizado, setActualizado] = useState<Date | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [offersFor, setOffersFor] = useState<{ dispatch: Dispatch; offers: Offer[] } | null>(null);
  const [pinging, setPinging] = useState<number | null>(null);

  // Aviso manual al chofer que ya tiene el turbo: sirve cuando dejó de
  // reportar. Si no tiene la app registrada, el mensaje dice el teléfono,
  // porque ahí lo único que funciona es llamarlo.
  const avisar = async (driverId: number) => {
    setPinging(driverId);
    setError(null);
    try {
      const json = await req(`/v2/drivers/${driverId}/ping`, { method: "POST", body: JSON.stringify({}) });
      setNotice(json.sent
        ? `Aviso enviado a ${json.driver_name ?? "el chofer"}`
        : `${json.driver_name ?? "El chofer"} no tiene la app registrada${json.phone ? `: llamalo al ${json.phone}` : ""}`);
    } catch (e: any) { setError(e.message); } finally { setPinging(null); }
  };
  const [assignFor, setAssignFor] = useState<Dispatch | null>(null);
  const [assignDriver, setAssignDriver] = useState("");
  const flash = (m: string) => { setNotice(m); window.setTimeout(() => setNotice(null), 4000); };

  const cargar = useCallback(async () => {
    try {
      const qs = new URLSearchParams();
      if (status) qs.set("status", status);
      if (date) qs.set("date", date);
      const r = await req(`/v2/turbo/dispatches${qs.toString() ? `?${qs}` : ""}`);
      setItems(r.dispatches ?? []);
      setActualizado(new Date());
      setError(null);
    } catch (e: any) { setError(e.message); }
  }, [req, status, date]);

  // Se refresca solo mientras la pestaña está a la vista: un turbo sin chofer
  // cambia de estado cada minuto.
  useEffect(() => {
    cargar();
    const t = window.setInterval(() => { if (document.visibilityState === "visible") cargar(); }, REFRESCO_MS);
    const onVisible = () => { if (document.visibilityState === "visible") cargar(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
  }, [cargar]);
  useEffect(() => { req("/v2/routes/drivers").then((d) => setDrivers(d.drivers ?? [])).catch(() => {}); }, [req]);

  const resumen = useMemo(() => {
    const pendientes = items.filter(sinChofer);
    return {
      sinChofer: pendientes.length,
      escalados: items.filter((d) => d.status === "ESCALATED").length,
      enCurso: items.filter((d) => d.status === "ASSIGNED" && ["ASSIGNED", "LOADED", "IN_PROGRESS"].includes(d.route_status)).length,
      terminados: items.filter((d) => d.route_status === "COMPLETED").length,
      maxEspera: pendientes.reduce((m, d) => Math.max(m, d.waiting_seconds ?? 0), 0),
    };
  }, [items]);
  // Los que no tienen chofer van arriba, y entre ellos el que más espera.
  const ordenados = useMemo(() => [...items].sort((a, b) => {
    const pa = sinChofer(a) ? 0 : 1, pb = sinChofer(b) ? 0 : 1;
    if (pa !== pb) return pa - pb;
    if (pa === 0) return (b.waiting_seconds ?? 0) - (a.waiting_seconds ?? 0);
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  }), [items]);

  const verOfertas = async (d: Dispatch) => {
    setBusy(d.route_id);
    try { const r = await req(`/v2/turbo/dispatches/${d.route_id}/offers`); setOffersFor({ dispatch: d, offers: r.offers ?? [] }); } catch (e: any) { setError(e.message); } finally { setBusy(null); }
  };
  const reofertar = async (d: Dispatch) => {
    setBusy(d.route_id);
    try {
      // Un programado se ofrece solo el día prometido: adelantarlo se pregunta.
      if (d.status === "SCHEDULED" && !window.confirm(`El #${d.route_id} es para el ${d.scheduled_date.slice(8, 10)}/${d.scheduled_date.slice(5, 7)}. ¿Ofrecerlo ahora igual?`)) return;
      if (!(await confirmarConTurboApagado(req, `Volver a ofrecer el #${d.route_id} a los choferes`))) return;
      const r = await req(`/v2/turbo/dispatches/${d.route_id}/reoffer`, { method: "POST", body: JSON.stringify({}) });
      flash(r.scheduled ? `#${d.route_id}: no hay choferes disponibles, sigue programado para su día` : r.escalated ? `#${d.route_id}: no hay choferes disponibles, sigue escalado` : `#${d.route_id} ofrecido a ${r.offers?.length ?? 0} choferes`);
      cargar();
    } catch (e: any) { setError(e.message); } finally { setBusy(null); }
  };
  // La asignación a mano del Turbo (la misma de «En vivo»): en una sola
  // transacción asigna y cierra las ofertas abiertas. Antes iba por el PUT de
  // Recorridos, que no cerraba las ofertas (un chofer que aceptaba segundos
  // después se lo quedaba) ni miraba quién lo tenía. Va con el chofer que se
  // ve en la fila: si en el medio lo aceptó alguien, el servidor no lo pisa
  // (409) y la lista se recarga.
  const asignar = async () => {
    if (!assignFor || !assignDriver) return;
    const d = assignFor;
    setBusy(d.route_id);
    try {
      const r = await req("/v2/turbo/dispatches/manual", { method: "POST", body: JSON.stringify({ delivery_id: d.delivery_id, driver_id: Number(assignDriver), expected_driver_id: d.driver_id ?? null }) });
      // Asignar a mano no frena: si no le entraba al chofer (tope o plan), se dice.
      const aviso = (r.warnings ?? []).filter((w: string) => w === "CAP_REACHED" || w === "NOT_FEASIBLE").map(turboWarning);
      flash(`#${d.route_id} asignado a mano; las ofertas abiertas se cerraron${aviso.length ? `. Ojo: ${aviso.join(" · ")}` : ""}`);
      setAssignFor(null); setAssignDriver("");
      cargar();
    } catch (e: any) {
      // Lo elegido ya no vale (409: lo tomó otro o se cerró): se cierra, se
      // recarga y queda a la vista el porqué (cargar limpia el error).
      setAssignFor(null); setAssignDriver("");
      await cargar();
      setError(e.message);
    } finally { setBusy(null); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Turbo · Despachos</h2>
            <p style={subtitleStyle}>Cada turbo que entró, a quién se le ofreció y quién lo tomó. Los que están sin chofer van primero: se pueden volver a ofrecer o asignar a mano.</p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <input id="turbo-fecha" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ background: "transparent", color: "var(--text-primary)", border: "1px solid var(--border)", borderRadius: "10px", padding: "8px 10px", font: "inherit" }} />
            {date ? <Button variant="secondary" onClick={() => setDate("")}>Todas las fechas</Button> : null}
            <Button variant="secondary" onClick={cargar}>Actualizar</Button>
            <Button variant="secondary" onClick={() => navigate("/admin/turbo/config")}>Configuración</Button>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "14px", marginTop: "18px" }}>
          <div><div style={smallMutedStyle}>Sin chofer</div><div style={{ ...kpiStyle, color: resumen.sinChofer ? "var(--warning, #d29922)" : "var(--text-primary)" }}>{resumen.sinChofer}</div></div>
          <div><div style={smallMutedStyle}>Escalados</div><div style={{ ...kpiStyle, color: resumen.escalados ? "var(--danger)" : "var(--text-primary)" }}>{resumen.escalados}</div></div>
          <div><div style={smallMutedStyle}>Con chofer, en curso</div><div style={kpiStyle}>{resumen.enCurso}</div></div>
          <div><div style={smallMutedStyle}>Terminados</div><div style={{ ...kpiStyle, color: "var(--success)" }}>{resumen.terminados}</div></div>
          <div><div style={smallMutedStyle}>Mayor espera</div><div style={{ ...kpiStyle, color: resumen.maxEspera > 300 ? "var(--danger)" : "var(--text-primary)" }}>{resumen.sinChofer ? fmtMin(resumen.maxEspera) : "—"}</div></div>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "14px", alignItems: "center" }}>
          <button type="button" style={chip(status === "")} onClick={() => setStatus("")}>Todos</button>
          {(Object.keys(STATUS) as DispatchStatus[]).map((s) => <button key={s} type="button" style={chip(status === s)} onClick={() => setStatus(s)}>{STATUS[s].label}</button>)}
          <span style={{ ...smallMutedStyle, marginLeft: "auto" }}>{actualizado ? `Actualizado ${actualizado.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })} · cada ${REFRESCO_MS / 1000} s` : "Cargando…"}</span>
        </div>
        {error ? <div style={{ marginTop: "10px", color: "var(--danger)" }}>{error}</div> : null}
        {notice ? <div style={{ marginTop: "10px", color: "var(--success)" }}>{notice}</div> : null}
      </Card>

      <Card>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Recorrido</th><th style={thStyle}>Estado</th><th style={thStyle}>Tienda &rarr; destino</th><th style={thStyle}>Distancia</th><th style={thStyle}>Entregar antes de</th><th style={thStyle}>Chofer</th><th style={thStyle}></th></tr></thead>
            <tbody>
              {ordenados.length === 0 ? <tr><td style={tdStyle} colSpan={7}><span style={smallMutedStyle}>Ningún despacho{status ? ` en estado «${STATUS[status].label}»` : ""}{date ? ` para ${date}` : ""}.</span></td></tr> : null}
              {ordenados.map((d) => {
                const st = STATUS[d.status];
                const vencido = d.seconds_to_deadline < 0;
                const puedeReofertar = d.route_status === "PLANNED" && d.status !== "OFFERING";
                const puedeAsignar = d.route_status === "PLANNED";
                const warnings = (d.warnings ?? []).map(turboWarning);
                return (
                  <tr key={d.route_id} style={{ background: d.status === "ESCALATED" ? "rgba(248,81,73,0.06)" : undefined }}>
                    <td style={tdStyle}><b>#{d.route_id}</b><div style={smallMutedStyle}>{d.scheduled_date} · entró {fmtTime(d.created_at)}</div></td>
                    <td style={tdEstadoStyle}>
                      <span style={badge(st.color)}>{st.label}</span>
                      <div style={smallMutedStyle}>recorrido {ROUTE_LABEL[d.route_status] ?? d.route_status}{d.ring ? ` · anillo ${d.ring}` : ""}{d.offers_count ? ` · ${d.offers_count} ofertas` : ""}</div>
                      {d.turbo_group_kind === "LOTE" && d.turbo_group_status === "OFFERING" ? <div style={{ ...smallMutedStyle, fontWeight: 600 }}>en el lote #{d.turbo_group_id}: se ofrece entero</div> : null}
                      {warnings.length ? <div style={{ ...smallMutedStyle, color: "var(--warning, #d29922)" }}>{warnings.join(" · ")}</div> : null}
                      {d.waiting_seconds != null && !d.driver_id ? <div style={{ ...smallMutedStyle, fontWeight: 600, color: d.waiting_seconds > 300 ? "var(--danger)" : d.waiting_seconds > 120 ? "var(--warning, #d29922)" : undefined }}>espera {fmtMin(d.waiting_seconds)}</div> : null}
                    </td>
                    <td style={tdStyle}>{d.store_name ?? (d.store_id ? `tienda ${d.store_id}` : "—")} <span style={smallMutedStyle}>&rarr;</span> {d.nombre_destinatario ?? "—"}<div style={smallMutedStyle}>{d.direccion ?? ""}{d.tracking ? ` · ${d.tracking}` : ""}</div></td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{km(d.distance_km)}</td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <b>{fmtTime(d.deadline_at)}</b>
                      {d.route_status !== "COMPLETED" && d.route_status !== "CANCELLED" ? <div style={{ ...smallMutedStyle, color: vencido ? "var(--danger)" : d.seconds_to_deadline < 1800 ? "var(--warning, #d29922)" : undefined }}>{vencido ? `vencido hace ${fmtMin(-d.seconds_to_deadline)}` : `faltan ${fmtMin(d.seconds_to_deadline)}`}</div> : null}
                    </td>
                    <td style={tdStyle}>{d.driver_id ? (
                      <>
                        {driverName({ username: d.driver_username ?? "", first_name: d.driver_first_name, last_name: d.driver_last_name })}
                        <div style={smallMutedStyle}>{d.driver_username}{d.turbo_group_id ? ` · grupo #${d.turbo_group_id}` : ""}</div>
                        {d.route_status !== "COMPLETED" && d.route_status !== "CANCELLED" ? (
                          <button type="button" style={linkBtnStyle} disabled={pinging === d.driver_id} onClick={() => avisar(d.driver_id as number)}>
                            {pinging === d.driver_id ? "avisando…" : "avisar al chofer"}
                          </button>
                        ) : null}
                      </>
                    ) : <span style={{ color: "var(--warning, #d29922)" }}>sin chofer</span>}</td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        <Button variant="secondary" disabled={busy === d.route_id} onClick={() => verOfertas(d)}>Ver ofertas</Button>
                        {puedeReofertar ? <Button variant="secondary" disabled={busy === d.route_id} onClick={() => reofertar(d)}>{d.status === "SCHEDULED" ? "Ofrecer ahora" : "Reofertar"}</Button> : null}
                        {puedeAsignar ? <Button disabled={busy === d.route_id} onClick={() => { setAssignFor(d); setAssignDriver(""); }}>Asignar a mano</Button> : null}
                        <Button variant="ghost" onClick={() => navigate(`/admin/recorridos?date=${d.scheduled_date}`)}>Recorrido</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {offersFor ? (
        <Modal title={<>Ofertas del turbo #{offersFor.dispatch.route_id} <span style={{ ...smallMutedStyle, fontWeight: 400 }}>· {offersFor.dispatch.store_name ?? ""}</span></>} onClose={() => setOffersFor(null)}>
          {offersFor.offers.length === 0 ? <div style={smallMutedStyle}>Todavía no se le ofreció a nadie{offersFor.dispatch.status === "ESCALATED" ? ": no había choferes habilitados y disponibles cerca" : ""}.</div> : (
            <div style={{ overflowX: "auto" }}>
              <table style={tableStyle}>
                <thead><tr><th style={thStyle}>Anillo</th><th style={thStyle}>Chofer</th><th style={thStyle}>Distancia</th><th style={thStyle}>Ofrecido</th><th style={thStyle}>Vencía</th><th style={thStyle}>Respuesta</th></tr></thead>
                <tbody>
                  {offersFor.offers.map((o) => {
                    const r = o.response ? RESPONSE_LABEL[o.response] : null;
                    const viva = !o.response && new Date(o.expires_at).getTime() > Date.now();
                    return (
                      <tr key={o.id}>
                        <td style={tdStyle}>{o.ring}{o.source === "STORE" ? <div style={smallMutedStyle}>asignado a la tienda</div> : o.source === "MANUAL" ? <div style={smallMutedStyle}>elegido por el coordinador</div> : o.search_ring ? <div style={smallMutedStyle}>búsqueda {o.search_ring}</div> : null}</td>
                        <td style={tdStyle}>{driverName(o)}<div style={smallMutedStyle}>{o.username}</div></td>
                        <td style={tdStyle}>{km(o.distance_km)}</td>
                        <td style={tdStyle}>{fmtTime(o.offered_at)}</td>
                        <td style={tdStyle}>{fmtTime(o.expires_at)}</td>
                        <td style={tdStyle}>{r ? <span style={badge(r.color)}>{r.label}{o.responded_at ? ` · ${fmtTime(o.responded_at)}` : ""}</span> : <span style={badge(viva ? "var(--warning, #d29922)" : "var(--text-secondary)")}>{viva ? "esperando respuesta" : "sin respuesta"}</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      ) : null}

      {assignFor ? (
        <Modal title={`Asignar a mano el turbo #${assignFor.route_id}`} onClose={() => setAssignFor(null)} width="560px">
          <div style={panelStyle}>
            <div style={smallMutedStyle}>{assignFor.store_name ?? "Tienda"} → {assignFor.direccion ?? "destino"} · entregar antes de {fmtTime(assignFor.deadline_at)}</div>
            <div style={{ marginTop: "10px" }}><DriverPicker drivers={drivers} value={assignDriver} onChange={setAssignDriver} disabled={busy === assignFor.route_id} /></div>
            <div style={{ ...smallMutedStyle, marginTop: "8px" }}>No hace falta que esté habilitado para Turbo ni disponible: la asignación a mano manda. Las ofertas abiertas se cierran en el momento.</div>
            <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
              <Button onClick={asignar} disabled={!assignDriver || busy === assignFor.route_id}>Asignar</Button>
              <Button variant="secondary" onClick={() => setAssignFor(null)}>Cancelar</Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
